/**
 * Global notification handler for real-time notifications.
 *
 * A SINGLE generic handler (no per-event-type allowlist): it fires for *any*
 * notification received over the websocket and renders the focus-adaptive
 * "alert me in real time on this computer" surface:
 * - Focused tab    → in-app toast (the `in_app` channel)
 * - Unfocused tab  → browser/OS desktop pop-up (the `desktop` channel)
 * - Capacitor      → native local notification for background awareness
 * - Bell ledger    → always updated, but as a SEPARATE concern (SystemNotificationsBell)
 *
 * Whether a desktop pop-up is shown is governed by the F2 payload contract: the
 * backend surfaces the enabled `channels` per recipient+event, and we pop up only
 * when `'desktop'` is among them (focus permitting). Direct `notify.*` helper
 * paths leave `channels` unset → legacy rule: WS receipt implies `desktop`-eligible.
 *
 * React Query cache-invalidation is deliberately kept OUT of the pop-up path —
 * modules own their own invalidation via `useNotificationHandler`. The two global
 * invalidations below have no other globally-mounted owner, so they live here as
 * a distinct `useNotificationHandler` call, not mixed into the alert logic.
 *
 * Mount this component once at the app level, inside both NotificationProvider
 * and RealtimeNotificationProvider.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useRealtimeNotifications } from './NotificationContext';
import { useNotificationHandler, type NotificationHandlerMap } from './useNotificationHandler';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../auth/AuthContext';
import { apiGet } from '../api/axiosInstance';
import { entityUrlContext, getEntityUrl } from '../utils/entityUrls';
import { applyNotificationDeepLinkTab } from './deepLinkTab';
import { localizedNotificationTitle } from './notificationTitle';
import {
  initNativeNotifications,
  showNativeNotification,
  onNativeNotificationTap,
  isNativeNotificationsAvailable,
} from './nativeNotifications';
import { playNotificationSound } from './notificationSound';
import { usePushNotifications } from './usePushNotifications';
import type { Notification, NotificationChannel } from './types';

/**
 * Request browser notification permission if not already granted.
 * Returns true if permission is granted.
 */
async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

/**
 * Strip HTML tags from a string to produce plain text.
 * Used for browser/native notifications which don't render HTML.
 */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.innerText || doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Show a browser desktop notification.
 */
function showDesktopNotification(
  title: string,
  body: string,
  tagId: string,
  onClick?: () => void
): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  const notification = new Notification(title, {
    body: stripHtml(body),
    icon: '/favicon.ico',
    tag: `panluma-notif-${tagId}`, // Unique tag per notification (de-dupes repeats)
  });

  if (onClick) {
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  }

  // Auto-close after 5 seconds
  setTimeout(() => notification.close(), 5000);
}

/**
 * Desktop pop-up eligibility from the F2 `channels` contract.
 * `null`/undefined channels = legacy direct-`notify.*` path → WS receipt implies
 * the notification is `desktop`-eligible (preserves pre-refactor behavior).
 */
function isDesktopEligible(channels?: NotificationChannel[] | null): boolean {
  if (channels == null) return true;
  return channels.includes('desktop');
}

/**
 * In-app (toast/bell) eligibility from the F2 `channels` contract.
 * `null`/undefined channels = legacy direct-`notify.*` path → `in_app`-eligible.
 */
function isInAppEligible(channels?: NotificationChannel[] | null): boolean {
  if (channels == null) return true;
  return channels.includes('in_app');
}

/**
 * True when the current location is already showing the notification's entity,
 * so we should not interrupt the user with a toast/pop-up. Compares the deep-link
 * path and requires every query param of the target to match the current URL.
 */
function isViewingEntity(
  targetUrl: string,
  location: { pathname: string; search: string },
): boolean {
  const [targetPath, targetQuery] = targetUrl.split('?');
  if (location.pathname !== targetPath) return false;
  if (!targetQuery) return true;
  const target = new URLSearchParams(targetQuery);
  const current = new URLSearchParams(location.search);
  for (const [key, value] of target) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

/** Subset of messaging user settings needed for notification sound control. */
interface MessagingNotificationSoundSettings {
  sound_enabled: boolean;
}

/**
 * Global cache-invalidations that keep AppBar surfaces fresh regardless of which
 * page is mounted (the per-page hooks like `useMessagingNotifications` only run on
 * their own page). Kept as a static, module-level map so subscriptions are stable.
 * This is the "separate concern" — never folded into the alert/pop-up handler.
 */
const GLOBAL_CACHE_INVALIDATIONS = {
  'message.new': { invalidate: [['messaging', 'unread-summary']] },
  'message.mentioned': { invalidate: [['messaging', 'unread-summary']] },
  'conversation.member_added': {
    invalidate: [
      ['messaging', 'unread-summary'],
      ['messaging', 'conversations'],
    ],
  },
  'agent.approval.requested': {
    // Keys must match OCTOPUS_KEYS in ai_agents/hooks/useOctopusApi.ts:
    // the global pending list + tenant inbox (list + sidebar badge count) +
    // the per-agent dashboard (HealthBanner count).
    invalidate: (n: Notification) => {
      const keys: (readonly unknown[])[] = [
        ['octopus', 'pending-approvals'],
        ['octopus', 'inbox-approvals'],
        ['octopus', 'inbox-approvals-count'],
      ];
      const agentId = n.data?.agent_id;
      if (typeof agentId === 'string') keys.push(['octopus', 'dashboard', agentId]);
      return keys;
    },
  },
  // An approval resolved / escalated / expired anywhere (another approver, the
  // lifecycle sweep) must clear it from THIS user's inbox + badge live.
  'agent.approval.resolved': {
    invalidate: (n: Notification) => {
      const keys: (readonly unknown[])[] = [
        ['octopus', 'pending-approvals'],
        ['octopus', 'inbox-approvals'],
        ['octopus', 'inbox-approvals-count'],
      ];
      const agentId = n.data?.agent_id;
      if (typeof agentId === 'string') keys.push(['octopus', 'dashboard', agentId]);
      return keys;
    },
  },
  'agent.approval.escalated': {
    invalidate: [
      ['octopus', 'pending-approvals'],
      ['octopus', 'inbox-approvals'],
      ['octopus', 'inbox-approvals-count'],
    ],
  },
  'agent.approval.expired': {
    invalidate: [
      ['octopus', 'pending-approvals'],
      ['octopus', 'inbox-approvals'],
      ['octopus', 'inbox-approvals-count'],
    ],
  },
} satisfies NotificationHandlerMap;

export function GlobalNotificationHandler() {
  const { subscribe, isConnected } = useRealtimeNotifications();
  const { showInfo } = useNotification();
  const { t } = useTranslation(['notifications']);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRequestedPermission = useRef(false);

  // Fetch messaging user settings to check sound_enabled
  const { data: messagingSettings } = useQuery({
    queryKey: ['messaging', 'settings', 'user'],
    queryFn: () => apiGet<MessagingNotificationSoundSettings>('/api/v1/messaging/settings/user'),
    staleTime: 5 * 60 * 1000, // 5 min — settings rarely change
    enabled: !!user,
  });
  const soundEnabledRef = useRef(true);
  useEffect(() => {
    soundEnabledRef.current = messagingSettings?.sound_enabled ?? true;
  }, [messagingSettings?.sound_enabled]);

  // Initialize FCM push notifications on Capacitor (Android/iOS)
  usePushNotifications();

  // Cache-invalidation as a SEPARATE concern (not in the pop-up path below).
  useNotificationHandler(GLOBAL_CACHE_INVALIDATIONS);

  // Use refs to avoid stale closures in the (stable) notification handler
  const userIdRef = useRef(user?.id);
  const locationRef = useRef(location);
  const showInfoRef = useRef(showInfo);
  const navigateRef = useRef(navigate);
  const tRef = useRef(t);

  // Keep refs in sync
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    showInfoRef.current = showInfo;
  }, [showInfo]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Request notification permission on mount (browser or native)
  useEffect(() => {
    if (isConnected && !hasRequestedPermission.current) {
      hasRequestedPermission.current = true;

      if (isNativeNotificationsAvailable()) {
        initNativeNotifications();
      } else if ('Notification' in window && Notification.permission === 'default') {
        // Note: Modern browsers may block this without user gesture
        requestNotificationPermission();
      }
    }
  }, [isConnected]);

  // Handle native notification taps (Capacitor) — navigate to the relevant page
  useEffect(() => {
    if (!isNativeNotificationsAvailable()) return;

    let cleanup: (() => void) | undefined;
    onNativeNotificationTap((data) => {
      if (data?.navigateTo) {
        navigateRef.current(data.navigateTo);
      }
    }).then((fn) => { cleanup = fn; });

    return () => { cleanup?.(); };
  }, []);

  // Generic alert handler: one subscription for ALL notification types.
  // Stable across renders (reads live values via refs).
  useEffect(() => {
    const handleNotification = (notification: Notification) => {
      // Never alert the actor about their own action.
      if (notification.actor_user_id === userIdRef.current) return;

      // The toast/pop-up surface needs a human-readable title. Transient refresh
      // cues (entity.thread.updated, typing.indicator) carry none — skip them.
      if (!notification.title) return;
      // Stored titles are persisted in English; re-derive a localized title from
      // the catalog by notification_type (falls back to the stored title).
      const title = localizedNotificationTitle(tRef.current, notification.type, notification.title);
      const body = notification.body ?? '';
      const tagId = notification.entity_id || notification.id;

      // Entity identity via the Entity URL Registry (null if unresolvable). Used
      // for the "already viewing" check — tab query params are NOT part of the
      // identity, so it stays the canonical entity path.
      const entityUrl =
        notification.entity_type && notification.entity_id
          ? getEntityUrl({
              entityType: notification.entity_type,
              entityId: notification.entity_id,
            })
          : null;

      // Navigation target on click/tap — a SEPARATE resolution from the identity
      // above: it feeds `data` in as template context, so a nested entity lands on
      // its specific page (a chatbot channel → the owning bot's Channels tab)
      // rather than the generic list, matching notificationHref()
      // (SystemNotificationsBell), resolvePushNavigationUrl and the backend email
      // CTA. Some types also deep-link to a specific tab (ticket message →
      // Messages, comment mention → Comments); centralized in
      // applyNotificationDeepLinkTab.
      const navTarget =
        notification.entity_type && notification.entity_id
          ? getEntityUrl({
              entityType: notification.entity_type,
              entityId: notification.entity_id,
              context: entityUrlContext(notification.data),
            })
          : null;
      const url = navTarget
        ? applyNotificationDeepLinkTab(navTarget, notification.type)
        : navTarget;

      // Don't interrupt a user already looking at the entity; the bell still
      // updates via its own SystemNotificationsBell subscription.
      if (entityUrl && isViewingEntity(entityUrl, locationRef.current)) return;

      const native = isNativeNotificationsAvailable();
      const desktopEligible = isDesktopEligible(notification.channels);
      const inAppEligible = isInAppEligible(notification.channels);
      const backgrounded = document.hidden || !document.hasFocus();

      // Sound is interruptive — gate it like the desktop channel. Native handles
      // its own sound, so skip the web sound on Capacitor.
      if (desktopEligible && soundEnabledRef.current && !native) {
        playNotificationSound();
      }

      if (native) {
        // Mobile: native local notification for background awareness.
        if (desktopEligible) {
          showNativeNotification(
            title,
            stripHtml(body),
            `panluma-notif-${tagId}`,
            url ? { navigateTo: url } : {},
            soundEnabledRef.current,
          );
        }
        return;
      }

      if (backgrounded) {
        // Unfocused tab → OS pop-up (gated on the desktop channel).
        if (desktopEligible) {
          showDesktopNotification(
            title,
            body,
            tagId,
            url ? () => navigateRef.current(url) : undefined,
          );
        }
      } else if (inAppEligible) {
        // Focused tab → in-app toast.
        showInfoRef.current(title);
      } else if (desktopEligible) {
        // Edge case: desktop-only recipient on a focused tab still gets the pop-up.
        showDesktopNotification(
          title,
          body,
          tagId,
          url ? () => navigateRef.current(url) : undefined,
        );
      }
    };

    return subscribe('*', handleNotification);
  }, [subscribe]);

  // This component doesn't render anything
  return null;
}
